//
//  publish-documentation.ts
//  Supernova CLI
//
//  Created by Jiri Trecak.
//  Copyright © Supernova.io. All rights reserved.
//

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports

import { Flags } from "@oclif/core"
import { SentryTraced } from "@sentry/nestjs"
import sdkTypes from "@supernovaio/sdk"
import { z, ZodType } from "zod"

import { SentryCommand } from "../types/index.js"
import { sleep } from "../utils/common.js"
import { getWritableVersion } from "../utils/sdk.js"

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Definition

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Configuration

const PublishDocumentationConfigSchema = z.object({
  apiKey: z.string(),
  awaitPublishJob: z.boolean(),
  designSystemId: z.string(),
  dev: z.boolean(),
  proxyUrl: z.string().url().optional(),
  target: z.string(),
})

type PublishDocumentationConfig = z.infer<typeof PublishDocumentationConfigSchema>

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation

/** Command that publishes documentation */
export class PublishDocumentation extends SentryCommand<PublishDocumentationConfig> {
  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command configuration

  // How this command can be run
  static aliases = ["publish-documentation"]
  // Command help description
  static description = "Publish latest version of the documentation"
  // Examples how to use the command
  static examples = [`$ @supernovaio/cli publish-documentation --apiKey="{xxx-xxx-xxx}" --designSystemId="{1234}"`]
  // Static flags to enable / disable features
  static flags = {
    apiKey: Flags.string({ description: "API key to use for accessing Supernova instance", required: true }),
    awaitPublishJob: Flags.boolean({
      default: true,
      description:
        "Whether to block the process until the publishing is done. " +
        "Setting the flag to false will exit with success as long as documentation publish was successfully triggered, " +
        "but before the publish is completed. Setting the flag to true will exit once the publish is complete and will " +
        "throw if documentation publish is not successful.",
    }),
    designSystemId: Flags.string({ description: "Design System to publish the documentation", required: true }),
    dev: Flags.boolean({ default: false, description: "When enabled, CLI will target dev server", hidden: true }),
    proxyUrl: Flags.string({
      description: "When set, CLI will use provided proxy URL for all requests",
      hidden: true,
      required: false,
    }),
    target: Flags.string({
      default: "Live",
      description: "Environment to use for publishing: Live or Preview",
      required: false,
    }),
  }

  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command runtime

  get commandId(): string {
    return PublishDocumentation.id
  }

  get configSchema(): ZodType<PublishDocumentationConfig> {
    return PublishDocumentationConfigSchema
  }

  @SentryTraced()
  async run(): Promise<void> {
    const { flags } = await this.parse(PublishDocumentation)

    const environment = tryParseDocsEnvironment(flags.target)
    if (!environment) {
      const supportedEnvs = [sdkTypes.DocumentationEnvironment.live, sdkTypes.DocumentationEnvironment.preview]
      this.error(`Unknown target ${flags.target}, must be one of [${supportedEnvs.join(", ")}]`)
    }

    // Get workspace -> design system –> version
    const { designSystem, id, instance } = await getWritableVersion(flags)

    this.log(`Queueing documentation publish in ${designSystem.name}...`)

    let publishJob = await instance.documentation.publishDrafts(id, environment, undefined)

    this.log(`Documentation queued for publishing`)

    if (!flags.awaitPublishJob) {
      this.log(`Documentation publish await is disabled, exiting before the publish is finished.`)
      return
    }

    this.log(`Waiting for the documentation publish to be finished...`)

    // Timeout is roughly 30 minutes
    for (let i = 0; i < 30 * 60; i++) {
      await sleep(1000)
      publishJob = await instance.documentation.getDocumentationBuild(designSystem.workspaceId, publishJob.id)
      if (isJobStatusDone(publishJob.status)) break
    }

    switch (publishJob.status) {
      case "Success": {
        this.log("\nDone: Documentation queued for publishing")

        break
      }

      case "Failed": {
        throw new Error(`Documentation publish failed`)
      }

      case "Timeout": {
        throw new Error(`Documentation publish timed out`)
      }

      default: {
        throw new Error(`Error awaiting publish job`)
      }
    }
  }
}

function tryParseDocsEnvironment(targetArg: string) {
  switch (targetArg.toLowerCase()) {
    case "live": {
      return sdkTypes.DocumentationEnvironment.live
    }

    case "preview": {
      return sdkTypes.DocumentationEnvironment.preview
    }

    default: {
      return null
    }
  }
}

function isJobStatusDone(status: sdkTypes.ExportBuildStatus): boolean {
  return (
    status === sdkTypes.ExportBuildStatus.Failed ||
    status === sdkTypes.ExportBuildStatus.Success ||
    status === sdkTypes.ExportBuildStatus.Timeout
  )
}
